import { AnthropicService } from '../../service/AnthropicService';
import { GeminiService } from '../../service/GeminiService';
import { GroqService } from '../../service/GroqService';
import { OpenAiService } from '../../service/OpenAiService';
import { PerplexityService } from '../../service/PerplexityService';
import { ReplicateService } from '../../service/ReplicateService';
import { XAiService } from '../../service/XAiService';
import { replacePlaceholders } from '../../util/myFunctionsCoreDisplay';
import {
  AiTextGeneratorFormValues,
  getProviderForModel,
} from '../AiConfigs/constants';

export type CommandTextNodePlaceholderSchema = Record<string, unknown>;

export const commandTextNodeResponses = async (
  e: AiTextGeneratorFormValues,
  schema: CommandTextNodePlaceholderSchema,
  flowId: string,
  technologies: any[] = [],
): Promise<unknown> => {
  const prompt = {
    prompt: replacePlaceholders(e.prompt, schema),
    content: replacePlaceholders(e.content, schema),
    instruction: replacePlaceholders(e.instruction, schema),
    persona: replacePlaceholders(e.persona, schema),
  };

  const aiSystem = getProviderForModel(e.aiModel, technologies) || e.aiSystem;

  let response = null;

  if (aiSystem === 'groq') {
    response = await GroqService.search({
      ...prompt,
      seed: +e.groqSeed,
      model: e.aiModel,
      temperature: +e.groqTemperature,
      max_tokens: +e.groqMaxTokens,
      top_p: +e.groqTopP,
      stop: e?.stop ? e?.stop : null,
      flowId,
      multimodalEnabled: e.multimodalEnabled !== false,
      autoToken: e.autoToken,
      reasoning: e.reasoning,
    });
  }

  if (aiSystem === 'opensources') {
    //falcon-40b-instruct
    //mpt-30b-chat
    // mpt-7b-storywriter
    // llama-2-70b-chat
    const options = {
      ...prompt,
      max_length: +e.max_length,
      temperature: +e.temperature,
      top_p: +e.top_p,
      repetition_penalty: +e.repetition_penalty,
      length_penalty: +e.length_penalty,
      no_repeat_ngram_size: +e.no_repeat_ngram_size,
      seed: +e.seed,
      flowId,
    };

    if (e.aiModel === 'falcon-40b-instruct') {
      response = await ReplicateService.falcon40bInstruct(options);
    }

    if (e.aiModel === 'mpt-7b-storywriter') {
      response = await ReplicateService.mpt7bStorywriter(options);
    }

    if (e.aiModel === 'mpt-30b-chat') {
      response = await ReplicateService.mpt30bChat({
        ...prompt,
        max_new_tokens: +e.max_new_tokens,
        flowId,
      });
    }

    if (e.aiModel === 'llama-2-70b-chat') {
      response = await ReplicateService.llama70bChat({
        // system_prompt: e.persona,
        ...prompt,
        max_new_tokens: +e.max_new_tokens,
        min_new_tokens: +e.min_new_tokens,
        temperature: +e.temperature,
        top_p: +e.top_p,
        top_k: +e.topK,
        seed: +e.seed,
        flowId,
      });
    }

    if (e.aiModel === 'code-llama-34b') {
      response = await ReplicateService.codeLlama34b({
        ...prompt,
        flowId,
      });
    }

    if (e.aiModel === 'code-llama-34b-instruct') {
      response = await ReplicateService.codeLlama34bInstruct({
        ...prompt,
        flowId,
      });
    }
  }

  if (aiSystem === 'openai') {
    response = await OpenAiService.login({
      ...prompt,
      model: e.aiModel,
      effort: e.aiEffort,
      fp: +e.aiFp,
      pp: +e.aiPp,
      tokens: +e.aiTokens,
      temperature: +e.aiTemperature,
      flowId,
      multimodalEnabled: e.multimodalEnabled !== false,
      autoToken: e.autoToken,
      reasoning: e.reasoning,
      isWebEnabled: e.isWebEnabled,
    });
  }

  if (aiSystem === 'perplexity') {
    const { data } = await PerplexityService.response({
      ...prompt,
      flowId,
      top_p: +e.perplexityTopP,
      top_k: +e.perplexityTopK,
      temperature: +e.perplexityTemperature,
      max_tokens: +e.perplexityTokens,
      model: e.aiModel,
      search_context_size: e.search_context_size,
      autoToken: e.autoToken,
    });

    response = {
      data: {
        message: data?.message,
        logs: data?.citations,
        usedTokens: data?.usedTokens,
      },
    };
  }

  if (aiSystem === 'anthropic') {
    const pathname = 'claude-v4';
    const model = e.aiModel;
    const useReasoningStream = e.reasoning === true;

    if (useReasoningStream) {
      response = await AnthropicService.reasoningStream({
        ...prompt,
        model,
        max_tokens: +e.anthropicToken,
        temperature: +e.anthropicTemperature,
        topP: +e.anthropicTopP,
        topK: +e.anthropicTopK,
        flowId,
        multimodalEnabled: e.multimodalEnabled !== false,
        autoToken: e.autoToken,
        reasoning: e.reasoning,
        isWebEnabled: e.isWebEnabled,
      });
    } else {
      response = await AnthropicService.response(
        {
          ...prompt,
          model,
          max_tokens: +e.anthropicToken,
          temperature: +e.anthropicTemperature,
          topP: +e.anthropicTopP,
          topK: +e.anthropicTopK,
          flowId,
          multimodalEnabled: e.multimodalEnabled !== false,
          autoToken: e.autoToken,
          reasoning: e.reasoning,
          isWebEnabled: e.isWebEnabled,
        },
        pathname,
      );
    }
  }

  if (aiSystem === 'gemini') {
    const { data } = await GeminiService.gemini({
      ...prompt,
      model: e.aiModel,
      temperature: +e.geminiTemp,
      topP: +e.geminiTopP,
      topK: +e.geminiTopK,
      maxOutputTokens: +e.geminiMaxOutputTokens,
      flowId,
      multimodalEnabled: e.multimodalEnabled !== false,
      autoToken: e.autoToken,
      reasoning: e.reasoning,
      isWebEnabled: e.isWebEnabled,
    });

    response = {
      data: {
        message: data.message,
        logs: data.thinking,
        usedTokens: data.usedTokens,
      },
    };
  }

  if (aiSystem === 'xai') {
    let pathname = 'grok-4';
    let model = e.aiModel;
    response = await XAiService.getCompletion(
      {
        ...prompt,
        model,
        effort: e.aiEffort,
        max_tokens: +e.aiTokens,
        temperature: +e.aiTemperature,
        flowId,
        type: 'chat',
        stream: e.streamEnabled,
        multimodalEnabled: e.multimodalEnabled !== false,
        autoToken: e.autoToken,
        reasoning: e.reasoning,
        isWebEnabled: e.isWebEnabled,
      },
      pathname,
    );
  }

  return response;
};
